import { RowTypes } from '@modeso/aumico-lib-annualreport-fe';
import type { ICustomerData } from '@modeso/aumico-lib-annualreport-fe';
import type { IStyleOptions } from '@modeso/types__aumico-lib-annualreport-be';

/**
 * PDFTron data payload sent to `POST /reporting/multiyear/pdf`.
 *
 * Built by `MultiYearPdfTronSwissMapper` (FE), opaque to `aumico-be`, consumed
 * by the multi-year `.docx` template (printer-ms).
 *
 * Naming mirrors the V1 print contract (`IPdfTronData`, `IFinancialTableData`):
 * everything `IMultiYearPdfTron*` is part of the print pipeline, never an
 * on-screen view-model.
 */
export interface IMultiYearPdfTronData {
    bs: IMultiYearPdfTronBalanceSheet;
    is: IMultiYearPdfTronIncomeStatement;
    /** True when the BS section should render in the PDF. Lets the template
     *  conditionally include the entire Bilanz block (`{#hasBs}…{/hasBs}`). */
    hasBs: boolean;
    /** Same for the Erfolgsrechnung. */
    hasIs: boolean;
    /**
     * Section id → localized title. Mirrors V1's `sectionIdToTitle` convention
     * so the template can reference `{sectionToTitle.BALANCE}` / `{sectionToTitle.INCOME}`
     * for headings without hard-coded German.
     */
    sectionToTitle: {
        BALANCE: string; // "Bilanz" / "Balance Sheet" / "Bilan"
        INCOME: string;  // "Erfolgsrechnung" / "Income statement" / "Compte de résultat"
    };
    /**
     * Color theme inherited from the master AR's `printOptions.styleOptions`.
     * Pass-through — the `.docx` template references fields directly via
     * `{colors.titleColor}`, `{colors.tableHeaderColor}`, etc. Includes
     * `backgroundColorPY` even though the multi-year layout doesn't have a
     * dedicated previous-year column — the template can decide how/where to
     * apply it. `undefined` when the master AR has no styleOptions; the
     * template should fall back to its own static styling in that case.
     */
    colors?: IStyleOptions;
    /**
     * Customer (= company) block, mirrors V1's `IPdfTronData.customer` 1:1
     * (`pdf-tron-swiss-mapper.ts:372` builds it via `getCustomerData()`).
     * Sourced from the master AR's `generalInformation.company`. The
     * `.docx` template references fields via `{customer.companyName}`,
     * `{customer.address}`, etc. Optional because the master AR may have
     * no company assigned (defensive — usually always present).
     */
    customer?: ICustomerData;
}

/**
 * Balance Sheet — single flat array. Aktiven and Passiven both live in `data`,
 * separated by an `EmptyRow`. Mirrors the on-screen Mehrjahresvergleich layout
 * (no separate "Total Aktiven" / "Total Passiven" totals — section labels
 * appear inline as `GroupHeader` rows at level 0).
 */
export interface IMultiYearPdfTronBalanceSheet {
    /** 2..5 — drives template column-visibility (FE-enforced via
     *  `MAX_SELECTED_YEARS`). Schema keeps 6 col slots for forward-compat. */
    yearCount: number;
    /** Whether `col{i}P` cells carry the Verhältniskennzahl ratio. */
    showRatios: boolean;
    /** Δ display mode — drives what each row's single `delta` field holds:
     *   `'pct'` → unit-less percentage; `'chf'` → unit-less absolute number.
     *  The unit lives in the template column header. */
    deltaMode: 'pct' | 'chf';
    /**
     * Flat row sequence:
     *   `TableHead` (column dates) → Aktiven block → `EmptyRow` → Passiven block.
     * Each block starts with a `GroupHeader` at level 0 (the section root) followed
     * by its descendants at level 1+.
     */
    data: IMultiYearPdfTronRow[];
}

/**
 * Income Statement — same single-array shape as BS.
 *
 * Layout: `TableHead` → IS descendants (top-down) → `GroupHeader` at level 0 for
 * the IS section root (the bottom "ER total" / Bilanzgewinn line, repositioned
 * to last to match the on-screen view's `isErTotal` rendering).
 */
export interface IMultiYearPdfTronIncomeStatement {
    yearCount: number;
    showRatios: boolean;
    deltaMode: 'pct' | 'chf';
    data: IMultiYearPdfTronRow[];
}

/**
 * Single row in any of the three arrays above.
 *
 * Always 6 col pairs present (unused = ''). Avoids per-cell template
 * conditionals — the template just renders all 6 cells; blanks render blank.
 *
 * For `rowType: 'TableHead'` rows, `col0V..col5V` carry the column DATE
 * strings (e.g. "31.12.2024" / "01.01.2024 – 31.12.2024") and `t` carries
 * the section title — same convention as the V1 swiss-pdf-tron-mapper.
 */
export interface IMultiYearPdfTronRow {
    rowType: RowTypes;
    /** Text label (FLI caption / group title / section title for TableHead). */
    t: string;

    /**
     * Indentation depth, mirrors the V1 swiss-pdf-tron-mapper convention so the
     * `.docx` template can branch on `{#level == N}…{/level == N}` for per-depth
     * styling. Reference levels:
     *   - TableHead / TableTotal      : 0
     *   - GroupHeader / GroupTotal    : 1 for top-level group, +1 per nested group
     *   - SubTotal                    : same as its sibling Rows (parent group + 1)
     *   - Row (FLI)                   : parent group's level + 1
     *   - SubRow / Of / Acc           : parent FLI's level + 1
     */
    level: number;

    col0V: string; col0P: string;
    col1V: string; col1P: string;
    col2V: string; col2P: string;
    col3V: string; col3P: string;
    col4V: string; col4P: string;
    col5V: string; col5P: string;

    /**
     * Δ value, pre-formatted unit-less. The parent section's `deltaMode`
     * decides what's in here:
     *   - `deltaMode === 'pct'` → percentage form, e.g. `"+12.5"` / `"-3.4"`
     *   - `deltaMode === 'chf'` → absolute form, e.g. `"+1'234.00"` / `"-12'345.00"`
     * The unit ("%" or currency) lives in the template column header so the
     * cell itself stays unit-less. Empty on `TableHead` and `EmptyRow`.
     */
    delta: string;
}
